import type { IssueSeverity } from '@/shared/constants/issue-status'

import { ageBandKey } from './dashboard-insights'
import { issueTypeHeadingFor } from './issue-type-catalog'
import { ageDays, isActive, matchesView, type IssueStatusView } from './issues'
import { RESOLUTION_TARGETS, resolutionStatus, type ResolutionTargets } from './resolution-targets'

import type { GisIssue } from '../api/types'

/**
 * The faceted working set for the Issues board. Values within a facet union (OR); different facets
 * combine with AND. Every facet is a multi-select array — `[]` means "all".
 */
export interface IssueFacets {
  statusView: IssueStatusView
  severity: readonly IssueSeverity[]
  category: readonly string[]
  /** Selected HEADINGS from the curated "Issue type" dropdown (`issue-type-catalog.ts`) — e.g.
   *  "Staff", "Infra". An issue matches when its `question` belongs to one of the selected
   *  headings' curated questions. Separate from `category`, which still filters by section_title
   *  everywhere else. Optional so existing call sites/fixtures stay valid. */
  issueType?: readonly string[]
  entity: readonly string[]
  /** Owning line-department names (from the issue's `department` field). */
  department: readonly string[]
  /** Canonical mandal keys (joined to each issue via its site). */
  mandal: readonly string[]
  /** HOD facet — DEPARTMENT names picked via the HOD filter (HOD ↔ department is 1:1; the feed only
   *  carries `department`). Separate from `department` so both chips read truthfully. Optional so
   *  existing call sites and fixtures stay valid. */
  hod?: readonly string[]
  /** Free-text query — case-insensitive match over title / ref / site / entity / department /
   *  category. Optional; empty/absent = no text filtering. */
  query?: string
  /** Pending-age band keys (`w1`…`m1`, see `AGE_BANDS`). Applies to an issue's age-since-raised.
   *  Optional; empty/absent = no age filtering. */
  age?: readonly string[]
  /** Site names (matched against the issue's `siteName`). Optional; empty/absent = no site filtering. */
  site?: readonly string[]
}

/** True when an issue value is inside a multi-select string facet (empty facet = "all"). */
const inFacet = (value: string | null, facet: readonly string[]): boolean =>
  facet.length === 0 || (value !== null && facet.includes(value))

/** Case-insensitive text match across an issue's searchable fields. */
function matchesQuery(i: GisIssue, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (needle === '') {
    return true
  }
  return [i.title, i.issueRef, i.siteName, i.entityName, i.department, i.category, i.question].some(
    (field) => field !== null && field.toLowerCase().includes(needle),
  )
}

/** One issue against every active facet (all checks AND together; each is cheap, so no
 *  short-circuit gymnastics). */
function matchesFacets(i: GisIssue, f: IssueFacets, siteMandal: Map<string, string>): boolean {
  const mandal = i.siteId !== null ? (siteMandal.get(i.siteId) ?? null) : null
  const checks = [
    matchesView(i.status, f.statusView),
    f.severity.length === 0 || f.severity.includes(i.severity),
    inFacet(i.category, f.category),
    inFacet(issueTypeHeadingFor(i.question), f.issueType ?? []),
    inFacet(i.entityName, f.entity),
    inFacet(i.department, f.department),
    inFacet(i.department, f.hod ?? []),
    inFacet(mandal, f.mandal),
    matchesQuery(i, f.query ?? ''),
    f.age === undefined || f.age.length === 0 || f.age.includes(ageBandKey(ageDays(i.createdAt))),
    inFacet(i.siteName, f.site ?? []),
  ]
  return checks.every(Boolean)
}

/** Apply every active facet to the feed (cross-filtering spine for the board + the map rings). */
export function filterByFacets(
  issues: GisIssue[],
  f: IssueFacets,
  siteMandal: Map<string, string>,
): GisIssue[] {
  return issues.filter((i) => matchesFacets(i, f, siteMandal))
}

/**
 * Count issues per mandal by joining each issue to its site's mandal (`siteMandal`). Feed the
 * ALREADY-faceted list so the counts track the active filters (the map's count-based choropleth +
 * labels, the count analogue of `attentionByName`). Issues with no site — or whose site's mandal is
 * unknown — can't be attributed and are skipped (same contract as `filterByFacets`'s mandal join).
 */
export function countByMandal(list: GisIssue[], siteMandal: Map<string, string>): Map<string, number> {
  const out = new Map<string, number>()
  for (const i of list) {
    if (i.siteId === null) {
      continue
    }
    const mandal = siteMandal.get(i.siteId)
    if (mandal === undefined) {
      continue
    }
    out.set(mandal, (out.get(mandal) ?? 0) + 1)
  }
  return out
}

/** Is an OPEN issue at/near its resolution target (the "Overdue" KPI + breach label trigger). */
const breaching = (i: GisIssue, targets: ResolutionTargets): boolean =>
  resolutionStatus(i.severity, ageDays(i.createdAt), targets) !== 'on-track'

/** Compact severity/age signals for the donut + 4 KPIs — computed over the OPEN subset of a list. */
export interface IssueSignals {
  unresolved: number
  criticalOpen: number
  avgAge: number
  /** Open issues at or within 3 days of their resolution target (V2 "Overdue" KPI). */
  dueOrOverdue: number
}

export function issueSignals(
  list: GisIssue[],
  targets: ResolutionTargets = RESOLUTION_TARGETS,
): IssueSignals {
  const open = list.filter(isActive)
  const criticalOpen = open.filter((i) => i.severity === 'critical').length
  const avgAge =
    open.length === 0
      ? 0
      : Math.round(open.reduce((sum, i) => sum + ageDays(i.createdAt), 0) / open.length)
  const dueOrOverdue = open.filter((i) => breaching(i, targets)).length
  return { unresolved: open.length, criticalOpen, avgAge, dueOrOverdue }
}

const SEV_RANK: Record<IssueSeverity, number> = { critical: 0, non_critical: 1 }

/** V2 risk score: open issues first, then breaching, then severity, then age. Closed sort to 0 (last). */
function riskScore(i: GisIssue, targets: ResolutionTargets): number {
  if (!isActive(i)) {
    return 0
  }
  return (
    (breaching(i, targets) ? 1000 : 0) + (2 - SEV_RANK[i.severity]) * 100 + ageDays(i.createdAt)
  )
}

/** Risk-sorted copy of the list (overdue → critical → oldest first). Does not mutate the input. */
export function issueRiskSort(
  list: GisIssue[],
  targets: ResolutionTargets = RESOLUTION_TARGETS,
): GisIssue[] {
  return [...list].sort((a, b) => riskScore(b, targets) - riskScore(a, targets))
}
